import { useState, useEffect } from 'react';
import {
  FiHelpCircle, FiSend, FiSearch, FiCheckCircle, FiHash,
  FiClock, FiAlertCircle, FiChevronDown,
  FiCalendar, FiLayers, FiInbox, FiTrendingUp, FiFileText,
  FiArchive, FiUsers, FiTag, FiBarChart2, FiEdit2,
  FiBookOpen, FiHeart, FiHome, FiGrid,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { submitSupport, checkSupportTicket, getMyTickets } from '../../services/api';
import { toast } from 'react-toastify';
import styles from './AppSupportPage.module.css';

// ── Role-specific help content ────────────────────────────────────────────────

const ADMIN_SECTIONS = [
  {
    icon: FiCalendar,
    title: 'Financial Years',
    path: '/accounting/financial-years',
    desc: 'Create and manage financial years for your Derasar. Mark one as "current" for active accounting. Close a year to lock it from edits. Every accounting record (receipts, payments, journals) belongs to a financial year.',
    steps: [
      'Go to Accounting → Financial Years',
      'Click "New Financial Year" and set the start/end dates',
      'Mark it as current to start recording transactions',
      'At year-end, close the year to lock it permanently',
    ],
  },
  {
    icon: FiLayers,
    title: 'Chart of Accounts',
    path: '/accounting/chart-of-accounts',
    desc: 'The chart of accounts is the backbone of your accounting. It organises your ledger into groups (Assets, Liabilities, Income, Expenses, Equity), sub-groups, and individual accounts.',
    steps: [
      'Go to Accounting → Chart of Accounts',
      'Default groups and accounts are auto-created when your Derasar is approved',
      'Add custom sub-groups (e.g. "Loans & Advances Given" under Assets)',
      'Create accounts under each sub-group — account codes are auto-generated',
    ],
  },
  {
    icon: FiTag,
    title: 'Donation Categories',
    path: '/management/categories',
    desc: 'Donation categories classify what each receipt or offering is for. Default categories (Dev Dravya, Jiv Daya, Angi, Sarva Sadharan) are pre-created. You can add more.',
    steps: [
      'Go to Administration → Donation Categories',
      'Click "Add Category" to create a new one',
      'Inactive categories can be reactivated later',
      'Categories are linked to income ledger accounts for automatic accounting',
    ],
  },
  {
    icon: FiUsers,
    title: 'Members & Families',
    path: '/users',
    desc: 'Manage members who have joined your Derasar. Approve or reject join requests, view all members, and organise them into family groups.',
    steps: [
      'Go to Administration → Members to see all members and pending requests',
      'Click "Approve" or "Reject" on pending join requests',
      'Go to Administration → Families to view and manage family groups',
      'Families help group members for donation tracking and reporting',
    ],
  },
  {
    icon: FiArchive,
    title: 'Offerings',
    path: '/accounting/offerings',
    desc: 'Offerings are offers made by members to donate a specific amount to a category. Track them from "pending" through "partial" to "completed".',
    steps: [
      'Go to Transactions → Offerings',
      'Create a new offering by selecting the member, category, and offered amount',
      'When payment is received, create a Receipt linked to the offering',
      'Status updates automatically: pending → partial → completed',
    ],
  },
  {
    icon: FiInbox,
    title: 'Receipts',
    path: '/accounting/receipts',
    desc: 'Record all money received by the Derasar. Supports cash, gold, item, anonymous, and ghee boli receipt types. Generate PDF receipts and email them to members.',
    steps: [
      'Go to Transactions → Receipts → New Receipt',
      'Select the member (or anonymous), date, category, amount and payment mode',
      'Optionally link to an existing offering',
      'Download the PDF receipt or email it directly to the member',
    ],
  },
  {
    icon: FiFileText,
    title: 'Payments',
    path: '/accounting/payments',
    desc: 'Record all money paid out by the Derasar — salaries, vendors, puja expenses, utilities, etc. Each payment creates a double-entry journal automatically.',
    steps: [
      'Go to Transactions → Payments → New Payment',
      'Select the expense account (e.g. Salary, Puja Expense), payment date and amount',
      'Choose the bank/cash account being debited',
      'Payments are auto-posted to the ledger',
    ],
  },
  {
    icon: FiEdit2,
    title: 'Journal Entries',
    path: '/accounting/journal-entries',
    desc: 'Manual double-entry adjustments for transactions that don\'t fit receipts or payments — such as loan given to an employee, fund transfers between accounts, bank deposits, or contra entries.',
    steps: [
      'Go to Transactions → Journal Entries → New Entry',
      'Select the Transaction Type (e.g. Loan Given, Fund Transfer, Cash Deposit)',
      'Choose debit and credit accounts, enter amount and narration',
      'The entry type is saved and shown in the list for easy filtering',
    ],
  },
  {
    icon: FiInbox,
    title: 'Ledger',
    path: '/accounting/ledger',
    desc: 'View the running balance and all transactions for any ledger account. Filter by financial year and date range.',
    steps: [
      'Go to Accounting → Ledger',
      'Search and select any account (Cash, Bank, Income, etc.)',
      'Filter by financial year and date range',
      'The ledger shows each debit/credit line with running balance',
    ],
  },
  {
    icon: FiTrendingUp,
    title: 'Reports',
    path: '/accounting/reports',
    desc: 'Generate accounting reports for a financial year: Balance Sheet, Income & Expenditure statement, Day Book, Cash Book, and Offering Aging.',
    steps: [
      'Go to Accounting → Reports',
      'Select the report type and financial year',
      'Balance Sheet shows Assets vs Liabilities + Equity',
      'I&E shows Income vs Expenditure for the period',
      'Offering Aging shows outstanding offering amounts by member',
    ],
  },
];

const MEMBER_SECTIONS = [
  {
    icon: FiHome,
    title: 'Dashboard',
    path: '/dashboard',
    desc: 'Your home screen shows a summary of your activity — outstanding offerings, recent receipts, and Derasar announcements at a glance.',
    steps: [],
  },
  {
    icon: FiGrid,
    title: 'My Derasar',
    path: '/derasar/my',
    desc: 'View your Derasar\'s details — name, location, admin contact, and your membership status. If you have multiple derasars, switch between them using the top bar.',
    steps: [
      'Use the Derasar selector in the top bar to switch active Derasar',
      'Go to Derasar → My Derasar to see details and your membership role',
    ],
  },
  {
    icon: FiUsers,
    title: 'My Family',
    path: '/family',
    desc: 'Manage your family group within the Derasar. You can create a family, invite members to join, or be added by a family head.',
    steps: [
      'Go to My Account → My Family',
      'Create a new family or request to join an existing one',
      'As family head, approve or reject join requests from other members',
      'Family members can be added directly without approval',
    ],
  },
  {
    icon: FiHeart,
    title: 'My Persons',
    path: '/my-persons',
    desc: 'Add household members (dependents like children or elderly) who don\'t have their own login. They appear in your profile and can be associated with donations.',
    steps: [
      'Go to My Account → My Persons',
      'Add name, relationship, and date of birth for each dependent',
    ],
  },
  {
    icon: FiArchive,
    title: 'Offerings (My Offers)',
    path: '/accounting/offerings',
    desc: 'View all offerings you have made to the Derasar. See how much you have paid vs your offered amount, and track the status of each offering.',
    steps: [
      'Go to Transactions → Offerings to see your offering history',
      'Each offering shows offered amount, amount paid, and current status',
      'Status: Pending → Partial → Completed or Cancelled',
      'The admin creates offerings on your behalf after you make an offer',
    ],
  },
  {
    icon: FiInbox,
    title: 'Receipts (My Donations)',
    path: '/accounting/receipts',
    desc: 'View all donation receipts issued to you. Download PDF copies or check the details of any past donation.',
    steps: [
      'Go to Transactions → Receipts',
      'Your receipts are listed with date, amount, category and receipt number',
      'Click any receipt to view full details and download the PDF',
    ],
  },
  {
    icon: FiBookOpen,
    title: 'Address Book',
    path: '/address-book',
    desc: 'Store and manage address entries — yours and your family members\'. Useful for records and communications.',
    steps: [
      'Go to My Account → Address Book',
      'Add new entries with name, address, phone, and notes',
    ],
  },
  {
    icon: FiBarChart2,
    title: 'My Reports',
    path: '/member-reports',
    desc: 'View your personal donation history and a summary of outstanding offerings across all financial years.',
    steps: [
      'Go to My Account → My Reports',
      'See your total donations, by category and by year',
      'Outstanding offering amounts are shown separately',
    ],
  },
  {
    icon: FiBookOpen,
    title: 'Events & Notice Board',
    path: '/derasar/events',
    desc: 'Stay updated with Derasar events and admin notices. Register for events and track your contributions.',
    steps: [
      'Go to Derasar → Events to see upcoming events',
      'Go to Derasar → Notice Board for announcements from your admin',
    ],
  },
];

const SUBJECTS = [
  'General Question', 'Technical Issue', 'Account / Login Problem',
  'Billing / Subscription', 'Donation / Payment Issue', 'Feature Request',
  'Derasar Registration Help', 'Other',
];

const STATUS_CONFIG = {
  open:        { label: 'Open',        color: '#2563eb' },
  in_progress: { label: 'In Progress', color: '#d97706' },
  resolved:    { label: 'Resolved',    color: '#16a34a' },
};

// ── Help accordion item ───────────────────────────────────────────────────────
const HelpItem = ({ section }) => {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;
  return (
    <div className={`${styles.helpItem} ${open ? styles.helpItemOpen : ''}`}>
      <button className={styles.helpItemHeader} onClick={() => setOpen(o => !o)}>
        <span className={styles.helpItemLeft}>
          <Icon size={16} className={styles.helpItemIcon} />
          <span className={styles.helpItemTitle}>{section.title}</span>
        </span>
        <FiChevronDown size={14} className={`${styles.helpItemChevron} ${open ? styles.helpItemChevronOpen : ''}`} />
      </button>
      {open && (
        <div className={styles.helpItemBody}>
          <p className={styles.helpItemDesc}>{section.desc}</p>
          {section.steps.length > 0 && (
            <ol className={styles.helpSteps}>
              {section.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const AppSupportPage = () => {
  const { user, isDerasarAdmin } = useAuth();
  const [tab, setTab] = useState('help'); // 'help' | 'ticket' | 'check'

  // Ticket form — name and email are locked to the logged-in user
  const [form, setForm]           = useState({ name: user?.name || '', email: user?.email || '', subject: '', message: '' });
  const [errors, setErrors]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  // My tickets list
  const [myTickets, setMyTickets]       = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState(null);

  // Manual check fallback
  const [ticketNo, setTicketNo]         = useState('');
  const [checking, setChecking]         = useState(false);
  const [ticketResult, setTicketResult] = useState(null);
  const [checkError, setCheckError]     = useState('');

  // Load my tickets when tab opens or after submission
  useEffect(() => {
    if (tab !== 'check') return;
    setTicketsLoading(true);
    getMyTickets()
      .then((res) => setMyTickets(res.data.data || res.data || []))
      .catch(() => setMyTickets([]))
      .finally(() => setTicketsLoading(false));
  }, [tab, submitted]);

  const isAdmin = isDerasarAdmin || user?.role === 'platform_admin' || user?.role === 'super_admin';
  const helpSections = isAdmin ? ADMIN_SECTIONS : MEMBER_SECTIONS;
  const roleLabel = isAdmin ? 'Derasar Admin' : 'Member';

  // ── Ticket submit ──
  const validateTicket = () => {
    const e = {};
    if (!form.name.trim())                       e.name    = 'Name is required';
    if (!form.email.trim())                      e.email   = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email))  e.email   = 'Enter a valid email';
    if (!form.subject)                           e.subject = 'Please select a subject';
    if (form.message.trim().length < 10)         e.message = 'Message must be at least 10 characters';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateTicket();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const res = await submitSupport(form);
      setSubmitted(res.data.data || res.data);
      setForm(f => ({ ...f, subject: '', message: '' }));
    } catch {
      toast.error('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Check status ──
  const handleCheck = async (e) => {
    e.preventDefault();
    if (!ticketNo.trim()) { setCheckError('Enter a ticket number'); return; }
    setChecking(true);
    setCheckError('');
    setTicketResult(null);
    try {
      const res = await checkSupportTicket(ticketNo.trim(), user?.email);
      setTicketResult(res.data.data || res.data);
    } catch (err) {
      setCheckError(err.response?.status === 404
        ? 'No ticket found with that number.'
        : 'Something went wrong. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

  return (
    <div className={styles.page}>
      <div className="page-header">
        <div>
          <h2><FiHelpCircle style={{ marginRight: 8 }} />Help &amp; Support</h2>
          <p>Instructions and support for {roleLabel} features</p>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button className={`${styles.tab} ${tab === 'help' ? styles.tabActive : ''}`} onClick={() => setTab('help')}>
          <FiHelpCircle size={14} /> How to Use
        </button>
        <button className={`${styles.tab} ${tab === 'ticket' ? styles.tabActive : ''}`} onClick={() => setTab('ticket')}>
          <FiSend size={14} /> Raise a Ticket
        </button>
        <button className={`${styles.tab} ${tab === 'check' ? styles.tabActive : ''}`} onClick={() => setTab('check')}>
          <FiSearch size={14} /> Check Ticket Status
        </button>
      </div>

      {/* ── How to Use ── */}
      {tab === 'help' && (
        <div className={styles.helpWrap}>
          <div className={styles.helpIntro}>
            <span className={styles.roleTag}>{roleLabel}</span>
            <span>Click any feature below to see how it works and how to get started.</span>
          </div>
          <div className={styles.helpList}>
            {helpSections.map((s) => <HelpItem key={s.title} section={s} />)}
          </div>
        </div>
      )}

      {/* ── Raise a Ticket ── */}
      {tab === 'ticket' && (
        <div className="card" style={{ maxWidth: 640 }}>
          {submitted ? (
            <div className={styles.successBox}>
              <FiCheckCircle size={36} className={styles.successIcon} />
              <h3>Ticket Submitted</h3>
              <p>Your support request has been received. Your ticket number is:</p>
              <div className={styles.ticketNo}><FiHash size={14} />{submitted.ticketNo}</div>
              <p className={styles.successNote}>Save this number to track your request. We respond within one business day.</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setTicketNo(submitted.ticketNo); setTab('check'); setTicketResult(null); }}>
                  <FiSearch size={13} /> Check Status
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => setSubmitted(null)}>
                  <FiSend size={13} /> Raise Another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Your Name</label>
                  <div className={styles.readonlyField}>{form.name}</div>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Your Email</label>
                  <div className={styles.readonlyField}>{form.email}</div>
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Subject *</label>
                <select
                  value={form.subject}
                  onChange={e => { setForm(f => ({ ...f, subject: e.target.value })); setErrors(er => ({ ...er, subject: '' })); }}
                >
                  <option value="">Select a subject…</option>
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.subject && <span className={styles.err}>{errors.subject}</span>}
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Message *</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={e => { setForm(f => ({ ...f, message: e.target.value })); setErrors(er => ({ ...er, message: '' })); }}
                  placeholder="Describe your issue or question in detail…"
                />
                {errors.message && <span className={styles.err}>{errors.message}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  <FiSend size={14} /> {submitting ? 'Submitting…' : 'Submit Ticket'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ── Check Ticket Status ── */}
      {tab === 'check' && (
        <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* My tickets list */}
          <div className="card">
            <div className="card-header"><h3>My Tickets</h3></div>
            {ticketsLoading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : myTickets.length === 0 ? (
              <div className="empty-state"><p>You have not raised any support tickets yet.</p></div>
            ) : (
              <div className={styles.ticketList}>
                {myTickets.map((t) => {
                  const cfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
                  const isOpen = expandedTicket === t.ticketNo;
                  return (
                    <div key={t.ticketNo} className={`${styles.ticketRow} ${isOpen ? styles.ticketRowOpen : ''}`}>
                      <button
                        className={styles.ticketRowHeader}
                        onClick={() => setExpandedTicket(isOpen ? null : t.ticketNo)}
                      >
                        <div className={styles.ticketRowLeft}>
                          <code className={styles.ticketRowNo}>{t.ticketNo}</code>
                          <span className={styles.ticketRowSubject}>{t.subject}</span>
                        </div>
                        <div className={styles.ticketRowRight}>
                          <span className={styles.statusBadge} style={{ color: cfg.color, borderColor: cfg.color + '40', background: cfg.color + '12' }}>
                            {t.status === 'open'        && <FiAlertCircle size={11} />}
                            {t.status === 'in_progress' && <FiClock size={11} />}
                            {t.status === 'resolved'    && <FiCheckCircle size={11} />}
                            {cfg.label}
                          </span>
                          <FiChevronDown size={14} className={`${styles.helpItemChevron} ${isOpen ? styles.helpItemChevronOpen : ''}`} />
                        </div>
                      </button>
                      {isOpen && (
                        <div className={styles.ticketRowBody}>
                          <div className={styles.ticketMeta}>
                            <span>Submitted: {fmt(t.createdAt)}</span>
                            {t.resolvedAt && <span>Resolved: {fmt(t.resolvedAt)}</span>}
                          </div>
                          {t.resolution && (
                            <div className={styles.ticketResolution}>
                              <div className={styles.ticketResolutionLabel}>Response from our team</div>
                              <p>{t.resolution}</p>
                            </div>
                          )}
                          {t.status !== 'resolved' && (
                            <p className={styles.ticketPending}>
                              <FiClock size={12} /> Our team is reviewing your request and will respond within one business day.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Manual lookup for older tickets */}
          <div className="card" style={{ padding: '16px 20px' }}>
            <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: 12 }}>
              Looking for a ticket raised before you logged in? Enter the ticket number below.
            </p>
            <form onSubmit={handleCheck}>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  value={ticketNo}
                  onChange={e => { setTicketNo(e.target.value.toUpperCase()); setCheckError(''); setTicketResult(null); }}
                  placeholder="e.g. TKT-20250423-0001"
                  style={{ fontFamily: 'monospace', letterSpacing: '0.04em', flex: 1 }}
                />
                <button type="submit" className="btn btn-primary" disabled={checking} style={{ flexShrink: 0 }}>
                  <FiSearch size={14} /> {checking ? 'Checking…' : 'Check'}
                </button>
              </div>
              {checkError && <span className={styles.err} style={{ marginTop: 6, display: 'block' }}>{checkError}</span>}
            </form>

            {ticketResult && (() => {
              const cfg = STATUS_CONFIG[ticketResult.status] || STATUS_CONFIG.open;
              return (
                <div className={styles.ticketCard}>
                  <div className={styles.ticketCardTop}>
                    <div>
                      <div className={styles.ticketCardNo}><FiHash size={12} />{ticketResult.ticketNo}</div>
                      <div className={styles.ticketCardSubject}>{ticketResult.subject}</div>
                    </div>
                    <span className={styles.statusBadge} style={{ color: cfg.color, borderColor: cfg.color + '40', background: cfg.color + '12' }}>
                      {ticketResult.status === 'open'        && <FiAlertCircle size={12} />}
                      {ticketResult.status === 'in_progress' && <FiClock size={12} />}
                      {ticketResult.status === 'resolved'    && <FiCheckCircle size={12} />}
                      {cfg.label}
                    </span>
                  </div>
                  <div className={styles.ticketMeta}>
                    <span>Submitted: {fmt(ticketResult.createdAt)}</span>
                    {ticketResult.resolvedAt && <span>Resolved: {fmt(ticketResult.resolvedAt)}</span>}
                  </div>
                  {ticketResult.resolution && (
                    <div className={styles.ticketResolution}>
                      <div className={styles.ticketResolutionLabel}>Response from our team</div>
                      <p>{ticketResult.resolution}</p>
                    </div>
                  )}
                  {ticketResult.status !== 'resolved' && (
                    <p className={styles.ticketPending}>
                      <FiClock size={12} /> Our team is reviewing your request and will respond within one business day.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppSupportPage;
